
import React, { useState, useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import AdminSidebar from "../sidebar/AdminSidebar";
import AdminHeader from "../header/AdminHeader";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { LeadProvider } from "@/context/LeadContext";
import { PartnerProvider } from "@/context/PartnerContext";
import { AssignmentProvider } from "@/context/AssignmentContext";

const AdminLayout: React.FC = () => {
  const isMobile = useIsMobile();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for admin session in localStorage
        const adminId = localStorage.getItem("adminId");
        const adminSession = localStorage.getItem("adminSession");

        if (!adminId || !adminSession) {
          setIsAuthenticated(false);
          return;
        }

        // Verify admin exists and is active
        const { data, error } = await supabase
          .from('administrators')
          .select('active')
          .eq('id', adminId)
          .single();

        if (error || !data || !data.active) {
          console.error("Admin authentication error:", error);
          setIsAuthenticated(false);
          return;
        }

        setIsAuthenticated(true);
      } catch (error) {
        console.error("Authentication check error:", error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-16 w-16 rounded-full bg-blue-500/10 animate-ping"></div>
            </div>
            <Loader2 className="relative h-12 w-12 animate-spin text-blue-600" />
          </div>
          <p className="mt-6 text-lg font-medium text-slate-700">Επαλήθευση πρόσβασης...</p>
          <p className="text-sm text-slate-500">Παρακαλώ περιμένετε</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return (
          <div className="min-h-screen flex bg-background">
            {/* Sidebar - Hidden on mobile, shown in drawer */}
            {!isMobile && <AdminSidebar />}

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
              <AdminHeader />
              <main className="flex-1 overflow-auto">
                <div className="px-4 md:px-6 lg:px-8 py-6 animate-fade-in">
                  <Outlet />
                </div>
              </main>
            </div>
          </div>
  );
};

export default AdminLayout;
